import React from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Download, FileText, Table as TableIcon } from "lucide-react";
import jsPDF from "jspdf";
import { useAuth } from "@/context/AuthContext";

export function ExportMenu({ data, filename, title, columns, buttonLabel = "Export" }) {
  const { canExport } = useAuth();
  const formatValue = (val) => {
    if (val === null || val === undefined) return '';
    
    // Check if it's an ISO date string (matches pattern like 2024-01-15T00:00:00.000Z)
    if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(val)) {
      return new Date(val).toLocaleDateString('en-GB'); // Returns DD/MM/YYYY or your locale
    }
    
    return val;
  };

  if (!canExport) return null;

  const exportToCSV = () => {
    const headers = columns.map(c => typeof c === 'object' ? c.header : c);
    const keys = columns.map(c => typeof c === 'object' ? c.key : c);

    const csvContent = [
      headers.join(','),
      ...data.map(row => 
        keys.map(key => {
          let val = row[key];
          return JSON.stringify(formatValue(val));
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToWord = () => {
    const headers = columns.map(c => typeof c === 'object' ? c.header : c);
    const keys = columns.map(c => typeof c === 'object' ? c.key : c);

    const tableRows = data.map(row => {
      return `<tr>${keys.map(key => {
        const val = formatValue(row[key]); // Format the value
        return `<td style="padding: 8px; border: 1px solid #ddd; word-wrap: break-word; max-width: 150px; font-size: 10pt;">${val !== null && val !== undefined ? val : ''}</td>`;
      }).join('')}</tr>`;
    }).join('');

    const tableHtml = `
      <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
      <head>
        <meta charset='utf-8'>
        <title>${title}</title>
        <style>
          @page {
            size: A4 landscape;
            margin: 1cm;
          }
          body { 
            font-family: 'Calibri', 'Arial', sans-serif; 
            font-size: 9pt; 
            line-height: 1.3;
            margin: 0;
            padding: 0;
          }
          table { 
            width: 100%; 
            border-collapse: collapse; 
            margin-top: 15px;
            table-layout: fixed;
          }
          th { 
            background-color: #f2f2f2; 
            color: #333; 
            font-weight: bold; 
            padding: 8px 6px; 
            border: 1px solid #999; 
            text-align: left;
            font-size: 9pt;
            word-wrap: break-word;
          }
          td { 
            padding: 8px 6px; 
            border: 1px solid #ddd; 
            vertical-align: top;
            font-size: 9pt;
            word-wrap: break-word;
            overflow-wrap: break-word;
          }
          h1 { 
            color: #333; 
            font-size: 14pt; 
            margin-bottom: 10px;
            margin-top: 0;
          }
          p {
            font-size: 8pt;
            margin: 5px 0;
          }
        </style>
      </head>
      <body>
        <h1>${title}</h1>
        <p>Generated: ${new Date().toLocaleString()}</p>
        <table>
          <thead>
            <tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr>
          </thead>
          <tbody>
            ${tableRows}
          </tbody>
        </table>
      </body>
      </html>
    `;

    const blob = new Blob([tableHtml], { type: 'application/msword' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.doc`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToPDF = () => {
    const doc = new jsPDF('landscape');
    const headers = columns.map(c => typeof c === 'object' ? c.header : c);
    const keys = columns.map(c => typeof c === 'object' ? c.key : c);
    
    doc.setFontSize(14);
    doc.text(title, 14, 15);
    doc.setFontSize(8);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 22);

    const tableData = data.map(row => 
      keys.map(key => {
        const val = formatValue(row[key]); // Format the value
        return String(val !== null && val !== undefined ? val : '');
      })
    );

    let y = 30;
    const xStart = 10;
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 10;
    const tableWidth = pageWidth - (2 * margin);
    
    const calculateColumnWidths = () => {
      const minWidth = 15;
      const maxWidth = tableWidth / headers.length;
      const calculatedWidths = headers.map((header, i) => {
        const samples = [header, ...tableData.slice(0, 5).map(row => row[i])];
        const maxLength = Math.max(...samples.map(s => String(s).length));
        const width = Math.min(Math.max(minWidth, maxLength * 2), maxWidth);
        return width;
      });
      
      const total = calculatedWidths.reduce((a, b) => a + b, 0);
      if (total > tableWidth) {
        const scale = tableWidth / total;
        return calculatedWidths.map(w => w * scale);
      }
      return calculatedWidths;
    };
    
    const colWidths = calculateColumnWidths();
    const rowHeight = 10;

    // Wrap each header once, up front, and size the header row tall enough
    // to fit every wrapped line — mirrors how data rows already compute
    // actualRowHeight below. Without this, doc.text(headerText[0], ...)
    // only drew the FIRST wrapped line of a header and silently dropped
    // the rest (e.g. "Monthly Payment" wrapping to ["Monthly","Payment"]
    // rendered only as "Monthly").
    const headerLines = headers.map((h, i) => doc.splitTextToSize(String(h), colWidths[i] - 3));
    const headerMaxLines = Math.max(1, ...headerLines.map(lines => lines.length));
    const headerRowHeight = Math.max((headerMaxLines * 4) + 4, rowHeight);

    const drawHeaderRow = () => {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      let hx = xStart;
      headers.forEach((h, i) => {
        doc.setFillColor(240, 240, 240);
        doc.rect(hx, y - 7, colWidths[i], headerRowHeight, 'F');
        doc.rect(hx, y - 7, colWidths[i], headerRowHeight, 'S');
        doc.text(headerLines[i], hx + 2, y - 3);
        hx += colWidths[i];
      });
      y += headerRowHeight - 7;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
    };

    drawHeaderRow();

    let xPos = xStart;

    tableData.forEach((row, rowIndex) => {
      let maxLines = 1;
      row.forEach((cell, i) => {
        const text = String(cell);
        const splitText = doc.splitTextToSize(text, colWidths[i] - 3);
        if (splitText.length > maxLines) maxLines = splitText.length;
      });

      const actualRowHeight = Math.max((maxLines * 4) + 4, rowHeight);

      if (y + actualRowHeight > pageHeight - margin) {
        doc.addPage();
        y = 20;
        drawHeaderRow();
      }

      const rowY = y;
      xPos = xStart;

      row.forEach((cell, i) => {
        const text = String(cell);
        
        doc.rect(xPos, rowY, colWidths[i], actualRowHeight, 'S');
        
        const splitText = doc.splitTextToSize(text, colWidths[i] - 3);
        doc.text(splitText, xPos + 2, rowY + 4);
        
        xPos += colWidths[i];
      });

      y += actualRowHeight;
    });

    doc.save(`${filename}.pdf`);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="gap-2" disabled={data.length === 0}>
          <Download className="w-4 h-4" />
          {buttonLabel}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={exportToCSV} disabled={data.length === 0}>
          <TableIcon className="w-4 h-4 mr-2" />
          Excel (CSV)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={exportToWord} disabled={data.length === 0}>
          <FileText className="w-4 h-4 mr-2" />
          Word (DOC)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={exportToPDF} disabled={data.length === 0}>
          <FileText className="w-4 h-4 mr-2" />
          PDF
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}